/**
 * Nederlands. De brontaal van het paneel.
 *
 * Vakjargon blijft Engels waar de Nederlandse radiowereld dat ook doet: "playlist",
 * "stream", "live", "podcast" en "slot" zijn wat een maker daadwerkelijk zegt.
 * "Afspeellijst" of "rechtstreeks" leest als machinevertaling en is trager te scannen,
 * en snel scannen is precies wat dit paneel moet kunnen tijdens een uitzending.
 */
export default {
  common: {
    yes: 'Ja',
    no: 'Nee',
    all: 'Alles',
    cancel: 'Annuleren',
    save: 'Opslaan',
    loading: 'Bezig met laden',
    unknown: 'Onbekend',
    today: 'Vandaag',
    previous: 'Vorige',
    next: 'Volgende',
    day: 'Dag',
    week: 'Week',
    active: 'Actief',
    archived: 'Gearchiveerd',
    minutes: '{count} min',
    signOut: 'Uitloggen',
    language: 'Taal',
    days: '{count} dagen',
    showing: '{count} van {total} getoond',
    showingLatest: 'Laatste {count} getoond',
    showingRange: '{from}–{to} van {total}',
    loadMore: 'Meer laden',
    wait: 'Wacht…',
    now: 'Nu',
    to: 'tot',
    readOnly: 'Alleen-lezen',
  },

  units: {
    minutes: '{count} min',
    seconds: '{count} s',
    hoursMinutes: '{hours} u {minutes} min',
    daysHours: '{days} d {hours} u',
    minuteShort: 'm',
  },

  contentTypes: {
    unknown: '—',
    show: 'Show',
    music: 'Muziek',
    jingle: 'Jingle',
    sweeper: 'Sweeper',
    commercial: 'Reclame',
    promo: 'Promo',
    news: 'Nieuws',
    podcast: 'Podcast',
    live: 'Live',
    emergency: 'Noodbron',
  },

  nav: {
    onAir: 'Uitzending',
    programming: 'Programmering',
    media: 'Media',
    podcasts: 'Podcasts',
    audience: 'Publiek',
    operations: 'Beheer',
    station: 'Station',

    dashboard: 'Dashboard',
    timeline: 'Uitzendlijn',
    requests: 'Verzoeken',
    liveStudio: 'Live studio',
    schedule: 'Rooster',
    clocks: 'Uurklokken',
    shows: 'Shows',
    episodes: 'Afleveringen',
    playlists: 'Playlists',
    playlistStudio: 'Playlist Studio',
    library: 'Bibliotheek',
    uploads: 'Uploads',
    recordings: 'Opnames',
    archive: 'Archief',
    distribution: 'Distributie',
    analytics: 'Statistieken',
    liveListeners: 'Live luisteraars',
    streamHealth: 'Streamstatus',
    systemHealth: 'Systeemstatus',
    alerts: 'Meldingen',
    jobs: 'Taken',
    auditLog: 'Audit log',
    domains: 'Eigen domein',
    streams: 'Streams & mounts',
    streamers: "DJ's / streamers",
    fallback: 'Noodbron',
    users: 'Gebruikers & rechten',
    settings: 'Instellingen',
    notifications: 'Alarmering',
    apiKeys: 'API-sleutels',

    collapse: 'Inklappen',
    expand: 'Uitklappen',
    tagline: 'Podcast & Radio Platform',
  },

  shell: {
    skipToControls: 'Naar de uitzendknoppen',
    apiUnreachable: 'Het paneel kan de API niet bereiken',
    apiUnreachableDetail:
      'De status hieronder kan verouderd zijn. De stream zelf heeft hier geen last van.',
    componentsNeedAttention:
      'Er is 1 onderdeel dat aandacht nodig heeft | Er zijn {count} onderdelen die aandacht nodig hebben',
    restoring: 'Sessie herstellen…',
    offlineDetail: 'Geen bron draagt de stream. Controleer de playout-runtime.',
    stationTime: 'Stationstijd',
    yourAccount: 'Je account en beveiliging',
  },

  player: {
    monitor: 'Meeluisteren',
    stopMonitoring: 'Stoppen met meeluisteren',
    mute: 'Dempen',
    unmute: 'Dempen opheffen',
    nothingOnAir: 'Niets in de uitzending',
    listeners: 'luisteraars',
    progress: 'Voortgang van het huidige item',
    live: 'Live',
    fallback: 'Fallback',
    offline: 'Offline',
    auto: '24/7',
  },

  onAir: {
    runtimeQueues: 'Runtime-wachtrijen',
    runtimeQueuesIntro:
      'Wat de playout-runtime klaarhoudt. Dit is niet de rotatie hierboven: een wachtrij legen haalt weg wat klaarstaat en laat wat nu speelt gewoon doorlopen.',
    queueLength: '{count} in de wacht',
    clearQueue: 'Legen',
    clearQueueConfirm: 'Deze wachtrij legen',
    queueName: {
      scheduled: 'Gepland',
      manual: 'Handmatig',
      traffic: 'Verkeer',
    },
    queue: 'Wachtrij (rotatie)',
    scheduled: 'Ingepland',
    title: 'Uitzending',
    skip: 'Huidig item overslaan',
    emergency: 'Noodbron activeren',
    emergencyConfirm: 'Naar de noodbron',
    clearEmergency: 'Noodbron uitschakelen',
    reasonRequired: 'Reden is verplicht',
    nextUp: 'Straks',
    stream: 'Stream',
    runtimeHealth: 'Systeemstatus',
    mount: 'Mount',
    format: 'Formaat',
    elapsed: 'Verstreken',
    remaining: 'Resterend',
    skipConfirm: 'Nu overslaan',
    nothingScheduled: 'Niets ingepland',
    nothingScheduledDetail: 'Het station blijft in de algemene rotatie tot er iets ingepland is.',
    noRuntimeReports: 'Nog geen statusmeldingen',
    noRuntimeReportsDetail: 'De playout-runtime heeft zijn status nog niet aan het paneel gemeld.',
    listeners: 'Luisteraars',
    unnamedItem: 'Naamloos item',
    noMedia: 'Aan dit slot is nog geen afspeelbare audio gekoppeld',
    stereo: 'stereo',
    mono: 'mono',
    rerun: 'Herhaling',
  },

  schedule: {
    title: 'Rooster',
    intro:
      'Kleur toont of een slot daadwerkelijk uitgezonden kan worden. Een blok met een vaste tijd wordt nooit verschoven.',
    range: 'Bereik',
    slotsNeedAttention:
      'Er is 1 slot dat aandacht nodig heeft | Er zijn {count} slots die aandacht nodig hebben',
    overlaps: 'Overlapt een ander slot',
    airs: 'Zendt uit',
    kind: 'Soort',
    status: 'Status',
    readiness: 'Gereed',
    exactTimeNote:
      'Dit is een item met een vaste tijd. Het rooster mag dit nooit verschuiven om uitloop op te vangen.',
    couldNotLoad: 'Het rooster kon niet geladen worden',
    couldNotMove: 'Dit slot kon niet verplaatst worden',
    freezeBlocked:
      'Dit slot valt binnen het venster van {minutes} minuten voor uitzending en je hebt geen overrulerecht.',
    overrideTitle: 'Dit slot gaat bijna de lucht in',
    overrideBody:
      '{show} gaat binnen {minutes} minuten de lucht in. Als je het nu verplaatst wordt dat met jouw reden op jouw account vastgelegd.',
    overrideLabel: 'Waarom verplaats je het?',
    overridePlaceholder: 'Aflevering kwam laat uit de studio',
    overrideConfirm: 'Verplaatsen en reden vastleggen',
    unnamedSlot: 'Naamloos slot',
    markers: {
      exactTime: 'Vaste tijd',
      rerun: 'Herhaling',
      locked: 'Binnen het bevriezingsvenster',
    },
    kinds: {
      PLANNED: 'Gepland',
      RESERVED: 'Gereserveerd',
      LIVE: 'Live',
      SPECIAL: 'Speciaal',
      RERUN: 'Herhaling',
      AD_BREAK: 'Reclameblok',
    },
    statuses: {
      SCHEDULED: 'Ingepland',
      CONFIRMED: 'Bevestigd',
      BROADCASTING: 'In de uitzending',
      BROADCAST: 'Uitgezonden',
      CANCELLED: 'Geannuleerd',
    },
    readinessState: {
      READY: 'Gereed',
      PENDING: 'Bezig',
      BLOCKED: 'Geblokkeerd',
      AIRED: 'Uitgezonden',
      NOT_APPLICABLE: 'Niet van toepassing',
    },
  },

  timeline: {
    title: 'Uitzendlijn',
    intro:
      'Wat gepland stond en wat er daadwerkelijk uitging, op dezelfde as. Een item dat afweek of niet liep is op beide sporen gemarkeerd.',
    window: 'Venster',
    hours: '±{count} uur',
    planned: 'Gepland',
    asRun: 'Uitgezonden',
    discrepancies:
      'Er is 1 item dat niet volgens plan is uitgezonden | Er zijn {count} items die niet volgens plan zijn uitgezonden',
    late: '{seconds}s te laat',
    early: '{seconds}s te vroeg',
    couldNotLoad: 'De uitzendlijn kon niet geladen worden',
    exactTimeItem: 'Item met vaste tijd',
    warning: 'Waarschuwing',
    states: {
      aired: 'Uitgezonden',
      on_air: 'In de uitzending',
      queued: 'In de wachtrij',
      locked: 'Vergrendeld',
      blocked: 'Geblokkeerd',
    },
  },

  shows: {
    create: 'Nieuw programma',
    createTitle: 'Nieuw programma',
    createHint:
      'Naam en adres zijn genoeg. De uitzenddag en duur bepalen hoe het programma in het rooster verschijnt zodra inplannen aanstaat.',
    name: 'Naam',
    slug: 'Adres (in URLs, kleine letters en streepjes)',
    description: 'Omschrijving',
    weekday: 'Uitzenddag',
    noWeekday: 'Geen vaste dag (eenmalig)',
    duration: 'Duur (minuten)',
    createConfirm: 'Programma aanmaken',
    manageTitle: 'Beheer',
    manageIntro:
      'Inplannen bepaalt of dit programma vanzelf in het rooster komt. Archiveren haalt het uit de lijst; het is altijd terug te draaien.',
    turnSchedulingOn: 'Inplannen aanzetten',
    turnSchedulingOff: 'Inplannen uitzetten',
    schedulingOnWarning:
      'Met inplannen aan komt {name} zonder tussenkomst in het rooster en op de zender.',
    schedulingOffWarning: 'Met inplannen uit maakt het rooster geen nieuwe slots meer voor {name}.',
    schedulingReason: 'Reden (komt in het logboek)',
    schedulingConfirm: 'Bevestigen',
    schedulingNowOn: 'Inplannen staat aan voor {name}.',
    schedulingNowOff: 'Inplannen staat uit voor {name}.',
    archive: 'Archiveren',
    archiveWarning:
      '{name} verdwijnt uit de actieve lijst en het rooster. Terugdraaien kan altijd.',
    archiveConfirm: 'Ja, archiveer {name}',
    archiveDone: '{name} is gearchiveerd.',
    cancel: 'Annuleren',
    title: 'Shows',
    intro:
      'Een show maakt alleen slots aan als inplannen aanstaat. Dat is bewust: een show die zichzelf per ongeluk gaat inplannen zet ongewenste audio in de uitzending.',
    which: 'Welke shows',
    schedulingOn: 'Inplannen AAN',
    schedulingOff: 'Inplannen UIT',
    empty: 'Nog geen shows.',
    emptyArchived: 'Geen gearchiveerde shows.',
    deleteTitle: 'Een show definitief beëindigen',
    delete: 'Verwijderen',
    deleteIntro:
      'Archiveren bewaart een show en zijn historie en haalt hem van het schema. Dit verwijdert hem, met zijn afleveringen, geplande uitzendingen en podcastfeed. Een feed die nog gepubliceerd is, moet eerst offline.',
    deleteWarning:
      'Het verwijderen van {name} wist de afleveringen, geplande uitzendingen en de podcastfeed. Dit kan niet ongedaan gemaakt worden.',
    deleteReason: 'Waarom',
    deletePassword: 'Je wachtwoord',
    deleteConfirm: '{name} definitief verwijderen',
    deleteDone: '{name} verwijderd, met {episodes} afleveringen en {slots} geplande uitzendingen.',
    open: '{name} openen',
    oneOff: 'Eenmalig',
    everyDay: 'Elke {day}',
    everyOtherDay: 'Om de week op {day}',
    everyNthDay: 'Elke {interval} weken op {day}',
    daily: 'Dagelijks',
    everyNDays: 'Elke {interval} dagen',
    monthly: 'Maandelijks',
    everyNMonths: 'Elke {interval} maanden',
  },

  episodes: {
    create: 'Nieuwe aflevering',
    hideForm: 'Formulier sluiten',
    createHint:
      'Eerst het record, dan de audio: die koppel je bij Uploads aan deze aflevering zodra het bestand er is.',
    show: 'Programma',
    pickShow: 'Kies een programma',
    number: 'Afleveringsnummer',
    episodeTitle: 'Titel',
    plannedDate: 'Geplande uitzenddatum',
    specialOption: 'Special (buiten de reguliere nummering, vraagt goedkeuring)',
    createConfirm: 'Aflevering aanmaken',
    created: 'Aflevering {number} {title} is aangemaakt.',
    title: 'Afleveringen',
    intro:
      'Een aflevering is gereed als de audio de analyse doorstaan heeft, niet als iemand hem op gereed heeft gezet. De audiokolom bepaalt of hij uitgezonden kan worden.',
    columnEpisode: 'Aflevering',
    columnShow: 'Show',
    columnPlanned: 'Gepland',
    columnAudio: 'Audio',
    columnStatus: 'Status',
    special: 'Speciaal',
    needsApproval: 'Wacht op goedkeuring',
    missing: 'Ontbreekt',
    empty: 'Geen afleveringen',
    emptyDetail: 'Upload audio en maak een aflevering aan om die in te plannen.',
    caption: 'Afleveringen',
    statuses: {
      DRAFT: 'Concept',
      COMPLETED: 'Uitgezonden',
      ARCHIVED: 'Gearchiveerd',
    },
    packTitles: '{count} titelwissels',
    packMismatch: 'Episode-pack: duur klopt niet',
    tracklist: 'Tracklist (CSV)',
  },

  playlists: {
    title: 'Playlists',
    intro:
      'De hoogste prioriteit wint als meerdere playlists in aanmerking komen. De noodplaylist doet nooit mee in de rotatie: die bestaat alleen voor de noodbron.',
    columnPlaylist: 'Playlist',
    columnType: 'Type',
    columnPriority: 'Prioriteit',
    columnOrder: 'Volgorde',
    columnItems: 'Items',
    columnState: 'Status',
    disabled: 'Uit',
    empty: 'Leeg',
    activeState: 'Actief',
    interrupts: 'Onderbreekt',
    emptyTitle: 'Geen playlists',
    emptyDetail: 'Maak een playlist aan zodat de rotatie iets heeft om uit te putten.',
    caption: 'Playlists',
    types: {
      GENERAL_ROTATION: 'Algemene rotatie',
      SCHEDULED: 'Ingepland',
      ONCE_PER_X_SONGS: 'Om de zoveel nummers',
      ONCE_PER_X_MINUTES: 'Om de zoveel minuten',
      ONCE_PER_HOUR: 'Eens per uur',
      JINGLE: 'Jingle',
      COMMERCIAL: 'Reclame',
      EMERGENCY: 'Noodbron',
    },
    orderModes: {
      SEQUENTIAL: 'Op volgorde',
      SHUFFLE: 'Geschud',
      RANDOM: 'Willekeurig',
      WEIGHTED: 'Gewogen',
    },
  },

  media: {
    columnTrack: 'Track',
    columnArtist: 'Artiest',
    untitled: 'Zonder titel',
    editTags: 'Bewerken',
    title: 'Mediabibliotheek',
    intro:
      'Elk bestand wordt bij het uploaden geanalyseerd. Een bestand dat niet decodeert wordt in quarantaine gezet in plaats van ingepland, want erachter komen tijdens de uitzending is geen optie.',
    columnFile: 'Bestand',
    columnLength: 'Lengte',
    columnFormat: 'Formaat',
    columnLoudness: 'Loudness',
    columnAnalysis: 'Analyse',
    columnHotCache: 'Playoutcache',
    staged: 'Klaargezet',
    notStaged: 'Niet klaargezet',
    emptyTitle: 'Nog geen media',
    emptyDetail: 'Upload audio om de bibliotheek te vullen.',
    caption: 'Mediabestanden',
    status: {
      PENDING: 'Wacht op analyse',
      ANALYZING: 'Wordt geanalyseerd',
      READY: 'Gereed',
      QUARANTINED: 'In quarantaine',
      MISSING: 'Ontbreekt',
    },
    staging: {
      NOT_STAGED: 'Niet klaargezet',
      STAGING: 'Wordt klaargezet',
      STAGED: 'Klaargezet',
      FAILED: 'Klaarzetten mislukt',
      EVICTED: 'Verwijderd uit cache',
    },
  },

  alerts: {
    title: 'Meldingen',
    intro:
      'Bevestigen betekent dat iemand ernaar kijkt. Oplossen betekent dat de oorzaak weg is. Die twee zijn gescheiden, want ze op één hoop gooien is hoe een incident vergeten wordt.',
    open: 'Open',
    everything: 'Alles',
    columnSeverity: 'Ernst',
    columnAlert: 'Melding',
    columnComponent: 'Onderdeel',
    columnSeen: 'Gezien',
    columnLastSeen: 'Laatst gezien',
    columnState: 'Status',
    acknowledge: 'Bevestigen',
    resolve: 'Oplossen',
    acknowledged: 'Bevestigd',
    resolved: 'Opgelost',
    openState: 'Open',
    emptyTitle: 'Er is niets mis',
    emptyDetail: 'Er staat geen melding open. Dit is de toestand die je wilt.',
    caption: 'Meldingen',
    resolveConfirm: 'Melding oplossen',
    severity: {
      CRITICAL: 'Kritiek',
      ERROR: 'Fout',
      WARNING: 'Waarschuwing',
      INFO: 'Info',
    },
  },

  audit: {
    title: 'Audit log',
    intro:
      'Elke ingrijpende handeling, met wie het deed en waarom. Regels worden geschreven door de diensten die de handeling uitvoeren en kunnen hier niet aangepast worden.',
    window: 'Venster',
    hours24: '24 uur',
    days: '{count} dagen',
    columnWhen: 'Wanneer',
    columnWho: 'Wie',
    columnAction: 'Handeling',
    columnOn: 'Op',
    columnReason: 'Reden',
    emptyTitle: 'Niets vastgelegd',
    emptyDetail: 'In dit venster is geen ingrijpende handeling uitgevoerd.',
    caption: 'Auditregels',
  },

  health: {
    title: 'Systeemstatus',
    intro: 'Per onderdeel, met hoe lang geleden het zichzelf gemeld heeft.',
    components: 'Onderdelen',
    noReports: 'Geen statusmeldingen',
    noReportsDetail: 'Nog geen enkel onderdeel heeft zijn status gemeld.',
    stale: 'De status kon niet ververst worden; dit is de laatst bekende stand.',
  },

  streams: {
    title: 'Streams & mounts',
    intro:
      'Clients vragen deze lijst op in plaats van een mount hard in te bouwen, zodat een andere codec of bitrate geen nieuwe app-release vereist.',
    columnName: 'Naam',
    columnCodec: 'Codec',
    columnBitrate: 'Bitrate',
    columnMount: 'Mount',
    columnAddress: 'Luister-URL',
    columnDefault: 'Standaard',
    default: 'Standaard',
    emptyTitle: 'Geen streams ingesteld',
    emptyDetail: 'Seed het station of voeg een output toe om een stream te publiceren.',
    caption: 'Beschikbare streamprofielen',
    columnState: 'Status',
    disabled: 'Uit',
    enable: 'Inschakelen',
    enableConfirm: 'Stream inschakelen',
    disable: 'Uitschakelen',
    disableConfirm: 'Stream uitschakelen',
    promote: 'Als standaard instellen',
    promoteConfirm: 'Standaard maken',
  },

  login: {
    email: 'E-mailadres',
    password: 'Wachtwoord',
    totp: 'Verificatiecode',
    signIn: 'Inloggen',
    signingIn: 'Bezig met inloggen',
    failed: 'Inloggen mislukt',
    trustDevice: 'Dit apparaat onthouden',
    trustDeviceDetail: 'Vertrouw dit apparaat en sla de code 30 dagen over',
    totpPrompt: 'Vul de zescijferige code uit je authenticator-app in.',
    recoveryCode: 'Herstelcode',
    recoveryPrompt: 'Vul een van je herstelcodes in. Elke code werkt één keer en vervalt daarna.',
    useRecoveryCode: 'Authenticator kwijt? Gebruik een herstelcode',
    useAuthenticator: 'Toch de authenticator gebruiken',
    failedRetry: 'Inloggen mislukt. Probeer het opnieuw.',
    verify: 'Verifiëren',
    verifying: 'Bezig met verifiëren…',
    startAgain: 'Opnieuw beginnen',
    errors: {
      invalidCredentials: 'Dit e-mailadres en wachtwoord horen niet bij elkaar.',
      totpInvalid: 'Die verificatiecode klopt niet.',
      totpReplayed: 'Die code is al gebruikt. Wacht op de volgende.',
      locked: 'Te veel pogingen. Dit account is enkele minuten geblokkeerd.',
      disabled: 'Dit account is uitgeschakeld. Vraag een beheerder.',
      expired: 'Deze inlogpoging is verlopen. Begin opnieuw.',
      network: 'Het paneel kan de API niet bereiken.',
      generic: 'Er ging iets mis. Probeer het opnieuw.',
    },
  },

  stations: {
    switch: 'Van station wisselen',
    none: 'Geen station',
    label: 'Station',
  },

  domains: {
    title: 'Eigen domein',
    intro:
      'Laat je luisteraars de stream bereiken op een naam van jezelf, bijvoorbeeld stream.mijnstation.nl, in plaats van op een adres van ons.',
    hostname: 'Domeinnaam',
    add: 'Toevoegen',
    addHint:
      'Een subdomein werkt het prettigst. Na toevoegen krijg je twee DNS-records die je bij je registrar zet.',
    none: 'Nog geen eigen domein voor dit station.',
    live: 'Actief',
    pending: 'Wacht op DNS',
    liveDetail: 'Het certificaat wordt automatisch geregeld; je hoeft niets te installeren.',
    publish:
      'Zet deze twee records in je DNS. De CNAME stuurt luisteraars hierheen, de TXT bewijst dat de naam van jou is.',
    recordType: 'Type',
    recordName: 'Naam',
    recordValue: 'Waarde',
    copy: 'Kopiëren',
    copied: 'Gekopieerd',
    check: 'DNS controleren',
    recheck: 'Opnieuw controleren',
    notYet: 'Nog niet compleet',
    remove: 'Verwijderen',
  },

  organisation: {
    title: 'Organisatie',
    intro:
      'Je organisatie, het abonnement en wie er toegang heeft. Wat je hier ziet aan verbruik zijn dezelfde getallen waar een limiet op zit.',
    stations: 'Stations',
    seats: 'Gebruikers',
    storage: 'Opslag',
    listeners: 'Piekluisteraars per station',
    quality: 'Streamkwaliteit',
    unlimited: 'Onbeperkt',
    noneTitle: 'Je hoort bij geen enkele organisatie.',
    noneDetail: 'Vraag de eigenaar van je organisatie om je account toe te voegen.',
    createTitle: 'Nieuwe organisatie',
    create: 'Organisatie aanmaken',
    createHint:
      'Een organisatie is een klant: eigen stations, eigen mensen, eigen abonnement. De eerste eigenaar moet al een account hebben; laat je het leeg, dan word je het zelf.',
    orgName: 'Naam',
    orgSlug: 'Adres (afgeleid van de naam als je dit leeg laat)',
    ownerEmail: 'E-mail van de eerste eigenaar',
    createConfirm: 'Aanmaken',
    allTitle: 'Alle organisaties',
    stationsTitle: 'Stations',
    noStations: 'Nog geen station.',
    addStation: 'Station toevoegen',
    addStationHint:
      'Een station aanmaken is alleen een regel in de database. De uitzendkant pakt het binnen een minuut op.',
    stationName: 'Naam',
    stationSlug: 'Adres (in de stream-URL)',
    addStationConfirm: 'Station aanmaken',
    membersTitle: 'Wie heeft toegang',
    addMember: 'Iemand toevoegen',
    addMemberHint:
      'Het account moet al bestaan: er is geen mailserver, dus een uitnodiging zou niemand ooit zien.',
    addMemberConfirm: 'Toevoegen',
    roleLabel: 'Rol',
    remove: 'Verwijderen',
    you: 'jij',
    twoFactorOn: '2FA aan',
    twoFactorOff: '2FA uit',
    signedInAs: 'Ingelogd als {email}',
    role: {
      owner: 'Eigenaar',
      admin: 'Beheerder',
      operator: 'Operator',
      user: 'Redactie',
      staff: 'Platformbeheer',
      viewer: 'Kijker',
    },
    planLabel: 'Abonnement',
    plan: {
      air: 'Air',
      signal: 'Signal',
      network: 'Network',
    },
  },

  security: {
    passwordTitle: 'Wachtwoord',
    passwordDetail:
      'Kies je eigen wachtwoord. Na het wijzigen worden al je andere sessies uitgelogd.',
    passwordChange: 'Wachtwoord wijzigen',
    currentPassword: 'Huidig wachtwoord',
    newPassword: 'Nieuw wachtwoord (minimaal 12 tekens)',
    newPasswordRepeat: 'Nieuw wachtwoord nogmaals',
    passwordMismatch: 'De twee nieuwe wachtwoorden zijn niet gelijk.',
    passwordConfirm: 'Wachtwoord wijzigen',
    passwordChanged: 'Wachtwoord gewijzigd. {count} andere sessie(s) uitgelogd.',
    title: 'Account & beveiliging',
    intro:
      'Tweestapsverificatie voor je eigen account. Na het inloggen met je wachtwoord vraagt het paneel om een code uit je authenticator-app.',
    account: 'Ingelogd als',
    stateOn: 'Tweestapsverificatie aan',
    stateOff: 'Tweestapsverificatie uit',
    since: 'Aangezet op',
    codesLeft: 'Herstelcodes over',
    codesLowTitle: 'Bijna geen herstelcodes meer',
    codesLowDetail:
      'Geef een nieuwe set uit zolang je nog kunt inloggen. Zonder codes en zonder telefoon heb je een beheerder nodig.',
    pending:
      'Er staat een niet-bevestigde koppeling klaar. Begin opnieuw om een nieuwe QR-code te krijgen.',
    enableTitle: 'Tweestapsverificatie aanzetten',
    enableDetail:
      'Je hebt een authenticator-app nodig, bijvoorbeeld 1Password, Bitwarden, Google Authenticator of de Wachtwoorden-app op je iPhone.',
    enable: 'Aanzetten',
    confirmPasswordTitle: 'Bevestig je wachtwoord',
    confirmPasswordDetail:
      'Een geopende sessie is niet genoeg om je tweede factor te verplaatsen naar een andere telefoon.',
    continue: 'Doorgaan',
    scanTitle: 'Scan de code',
    scanDetail:
      'Scan deze QR-code met je authenticator-app en vul daarna de zescijferige code in die de app laat zien.',
    manualTitle: 'Handmatig invoeren',
    manualDetail: 'Lukt scannen niet, typ dan deze sleutel over in je app.',
    activate: 'Bevestigen en aanzetten',
    codesTitle: 'Je herstelcodes',
    codesWarningTitle: 'Bewaar deze codes nu',
    codesWarningDetail:
      'Dit is de enige keer dat je ze ziet. Zonder deze codes en zonder je telefoon kan alleen een beheerder je weer binnenlaten. Er is geen mailserver die ze kan opsturen.',
    codesCopy: 'Kopiëren',
    codesStored: 'Ik heb ze bewaard',
    toOrganisation: 'Naar je organisatie en abonnement',
    manageTitle: 'Beheren',
    reissue: 'Nieuwe herstelcodes',
    reissueDetail:
      'Een nieuwe set maakt de vorige onbruikbaar. Gooi een eerder uitgeprint vel dus weg.',
    reissueConfirm: 'Nieuwe set uitgeven',
    disable: 'Uitzetten',
    disableDetail:
      'Je wachtwoord en een geldige code, allebei. Daarna beschermt alleen je wachtwoord dit account nog.',
    disableConfirm: 'Tweestapsverificatie uitzetten',
    errors: {
      codeInvalid: 'Die verificatiecode klopt niet.',
      codeReplayed: 'Die code is net al gebruikt. Wacht tot je app de volgende laat zien.',
      passwordWrong: 'Dat wachtwoord klopt niet.',
    },
  },

  users: {
    title: 'Gebruikers & rechten',
    intro:
      'Accounts voor het paneel. Een nieuw of gereset wachtwoord verschijnt precies één keer; het wordt alleen als hash bewaard en is daarna door niemand meer op te vragen.',
    columnName: 'Naam',
    columnRole: 'Rol',
    columnTotp: 'Tweestapsverificatie',
    columnLastLogin: 'Laatst ingelogd',
    columnState: 'Status',
    caption: 'Accounts',
    emptyTitle: 'Geen accounts',
    emptyDetail: 'Maak een account aan om iemand toegang tot het paneel te geven.',
    create: 'Nieuw account',
    createConfirm: 'Account aanmaken',
    fieldEmail: 'E-mailadres',
    fieldName: 'Naam',
    fieldRole: 'Rol',
    role: {
      viewer: 'Kijker',
      user: 'Redactie',
      operator: 'Operator',
      admin: 'Beheerder',
      superadmin: 'Superadmin',
    },
    totpOn: 'Aan',
    totpOff: 'Uit',
    totpReset: 'Tweestapsverificatie wissen',
    totpResetConfirm: 'Wissen en overal uitloggen',
    never: 'Nooit',
    inactive: 'Inactief',
    deactivate: 'Deactiveren',
    deactivateConfirm: 'Account deactiveren',
    reactivate: 'Opnieuw activeren',
    resetPassword: 'Wachtwoord resetten',
    resetConfirm: 'Nieuw wachtwoord uitgeven',
    changeRole: 'Rol wijzigen naar {role}',
    changeRoleConfirm: 'Rol wijzigen',
    reasonLabel: 'Reden (komt in het audit-logboek)',
    passwordIssued: 'Wachtwoord voor {email}',
    passwordOnce:
      'Wordt één keer getoond en is daarna niet meer op te vragen. Geef het veilig door.',
    passwordDismiss: 'Ik heb het genoteerd',
  },

  streamers: {
    title: 'DJ’s / streamers',
    intro:
      'Broncredentials voor de live-ingang. Hiermee logt de encoder van een presentator in — niet het paneel. Een kwijtgeraakt wachtwoord wordt gereset, nooit opgezocht.',
    columnName: 'Naam',
    columnUsername: 'Gebruikersnaam',
    columnShow: 'Programma',
    columnRecord: 'Opnemen',
    columnState: 'Status',
    caption: 'Streameraccounts',
    emptyTitle: 'Nog geen DJ-accounts',
    emptyDetail: 'Maak een account aan zodat een presentator live kan.',
    create: 'Nieuwe DJ',
    createConfirm: 'DJ aanmaken',
    fieldName: 'Naam',
    fieldUsername: 'Brongebruikersnaam',
    fieldShow: 'Gekoppeld programma',
    fieldRecord: 'Sessies opnemen',
    anyShow: 'Elk programma',
    showsHint:
      'Een presentator met shows kan alleen live gaan als een van die shows in het rooster staat, van een kwartier voor het blok tot tien minuten erna; daarna haalt de zender ze van de lucht. Een presentator zonder shows mag verbinden wanneer de vensters het toestaan.',
    editShows: 'Shows…',
    recordOn: 'Opnemen aan',
    recordOff: 'Opnemen uit',
    resetCredential: 'Wachtwoord resetten',
    disable: 'Blokkeren',
    disableConfirm: 'Account blokkeren',
    enable: 'Deblokkeren',
    enableConfirm: 'Account deblokkeren',
    credentialIssued: 'Broncredential voor {username}',
    credentialOnce: 'Wordt één keer getoond. De DJ vult dit in als wachtwoord in de encoder.',
  },

  requests: {
    title: 'Verzoeken',
    intro:
      'Wat luisteraars hebben aangevraagd. De engine heeft de scheidingsregels, de geldigheidsvensters en eventuele blokkades al toegepast, dus een verzoek dat hier wacht is er \u00e9\u00e9n die daar doorheen kwam en een mens nodig heeft.',
    columnWhen: 'Aangevraagd',
    columnTrack: 'Track',
    columnRequester: 'Aanvrager',
    columnStatus: 'Status',
    columnAction: 'Actie',
    caption: 'Muziekverzoeken',
    emptyTitle: 'Niets in de wacht',
    emptyDetail: 'Verzoeken vanaf de stationspagina verschijnen hier zodra luisteraars ze doen.',
    filterAll: 'Alles',
    approve: 'Goedkeuren',
    reject: 'Afwijzen',
    status: {
      PENDING: 'Wacht',
      APPROVED: 'Goedgekeurd',
      REJECTED: 'Afgewezen',
      PLAYED: 'Gespeeld',
      EXPIRED: 'Verlopen',
    },
  },
  recordings: {
    title: 'Opnames',
    intro:
      'Airchecks van live-sessies. Een voltooide opname is een gewoon media-bestand: hij gaat door dezelfde analyse en dezelfde opslag als al het andere.',
    columnWhen: 'Opgenomen',
    columnStreamer: 'DJ',
    columnFile: 'Bestand',
    columnLength: 'Duur',
    columnSize: 'Grootte',
    columnStatus: 'Analyse',
    caption: 'Opnames',
    emptyTitle: 'Nog geen opnames',
    emptyDetail: 'Zodra een DJ met opnemen aan live gaat, verschijnt de aircheck hier.',
  },

  liveListeners: {
    title: 'Luisteraars nu',
    intro:
      'Rechtstreeks uit de metingen van de streammonitor. De grafiek zet “nu” in context: veertig luisteraars betekent iets anders om 03:00 dan in de spits.',
    now: 'Nu luisterend',
    peak3h: 'Piek (3 uur)',
    perMount: 'Per stream',
    noMounts: 'Nog geen metingen.',
    lastThreeHours: 'Afgelopen drie uur',
    noHistory: 'Nog geen geschiedenis in dit venster.',
    chartLabel: 'Luisteraars over de afgelopen drie uur, piek {peak}',
    peakShort: 'piek',
    stale: 'De laatste meting is van {time} en ouder dan twee minuten; “nu” is onbekend.',
  },

  jobs: {
    title: 'Taken',
    intro:
      '“Analyse is traag” en “analyse ligt eruit” zijn twee verschillende avonden. Deze pagina laat zien wat de achtergrondwerkers doen; ingrijpen gebeurt bij de worker, niet met een knop hier.',
    columnQueue: 'Wachtrij',
    columnActive: 'Bezig',
    columnWaiting: 'Wachtend',
    columnDelayed: 'Uitgesteld',
    columnCompleted: 'Voltooid',
    columnFailed: 'Mislukt',
    caption: 'Achtergrondwachtrijen',
    emptyTitle: 'Geen wachtrijen zichtbaar',
    emptyDetail: 'De wachtrij-informatie kon niet worden gelezen.',
    failuresIn: 'Recente fouten in {queue}',
    attempts: '{count} pogingen',
  },

  streamHealth: {
    title: 'Streamgezondheid',
    intro:
      'Het audiopad van bron tot luisteraar. De runtime-status komt uit Liquidsoap; de alarmen komen van de monitor die meeluistert zoals een luisteraar. Spreken ze elkaar tegen, geloof dan de monitor.',
    runtimeUnreachable:
      'De playout-runtime is niet bereikbaar. De stream zelf kan gewoon doorlopen.',
    fallbackActive: 'De fallback draagt de uitzending. Er hoort geplande audio te spelen.',
    emergencyMode: 'De noodbron is actief.',
    mode: 'Modus',
    uptime: 'Runtime {uptime} in de lucht',
    live: 'Live-bron',
    liveConnected: 'Verbonden',
    liveIdle: 'Geen',
    fallback: 'Fallback',
    fallbackOn: 'Actief',
    fallbackOff: 'Stand-by',
    outputs: 'Uitgangen',
    connected: 'Verbonden',
    disconnected: 'Losgekoppeld',
    noOutputs: 'Geen uitgangen gemeld.',
    queues: 'Runtime-wachtrijen',
    queueItems: '{count} items',
    alerts: 'Openstaande streamalarmen',
    noAlerts: 'Geen streamalarm open. Zo hoort het.',
  },

  podcastFeeds: {
    title: 'Podcasts',
    intro:
      'Een feed hoort bij een programma en publiceert de uitgezonden afleveringen. Publiceren is een schakelaar, geen bijwerking: vul de feed eerst, zet hem dan open.',
    columnTitle: 'Feed',
    columnShow: 'Programma',
    columnCategory: 'Categorie',
    columnTrigger: 'Publicatie',
    columnState: 'Status',
    caption: 'Podcastfeeds',
    emptyTitle: 'Nog geen feeds',
    emptyDetail: 'Maak een feed aan om afleveringen als podcast aan te bieden.',
    create: 'Nieuwe feed',
    createConfirm: 'Feed aanmaken',
    fieldShow: 'Programma',
    chooseShow: 'Kies een programma',
    fieldTitle: 'Titel',
    fieldSlug: 'Slug',
    fieldDescription: 'Omschrijving',
    fieldAuthor: 'Auteur',
    fieldOwnerName: 'Eigenaar',
    fieldOwnerEmail: 'E-mailadres eigenaar',
    fieldCategory: 'Categorie',
    published: 'Gepubliceerd',
    unpublished: 'Niet gepubliceerd',
    publish: 'Publiceren',
    unpublish: 'Intrekken',
  },

  distribution: {
    title: 'Distributie',
    intro:
      'Directories abonneren zich op de feed-URL en verder op niets. Deze pagina bestaat om die URL foutloos door te geven aan Apple, Spotify en elke app die hen volgt.',
    publishedFeeds: 'Gepubliceerde feeds',
    nonePublished: 'Nog geen gepubliceerde feed. Publiceer er een op de podcastpagina.',
    unpublishedFeeds: 'Nog niet gepubliceerd',
    publishFirst: 'Publiceer de feed voordat je hem aanmeldt bij een directory.',
    copy: 'Kopieer URL',
    copied: 'Gekopieerd',
    webhooks: 'Webhooks',
    webhooksIntro:
      'Externe systemen die een bericht krijgen bij gebeurtenissen zoals een nieuwe publicatie.',
    noWebhooks: 'Geen webhooks geconfigureerd.',
  },

  podcastAnalytics: {
    title: 'Podcaststatistieken',
    intro:
      'Downloads per aflevering, met IP-adressen teruggebracht tot pseudoniemen voordat er iets wordt bewaard. “Gedeeltelijk” zijn range-verzoeken die nooit het gros van het bestand ophaalden — apps die voorladen, geen luisteraars.',
    feed: 'Feed',
    total: '{count} downloads in dit venster',
    columnEpisode: 'Aflevering',
    columnDownloads: 'Downloads',
    columnPartial: 'Gedeeltelijk',
    caption: 'Downloads per aflevering',
    emptyTitle: 'Nog geen downloads',
    emptyDetail: 'Zodra een aflevering wordt opgehaald, verschijnen de aantallen hier.',
  },

  audienceAnalytics: {
    title: 'Luistercijfers',
    intro:
      'Afgeleid van geanonimiseerde luistersessies: IP-adressen worden pseudoniemen vóór er iets wordt bewaard. De inhoudstabel rekent met wat werkelijk uitzond — een programma dat te laat begon, bereikte zijn publiek te laat.',
    uniqueListeners: 'Unieke luisteraars',
    peakConcurrent: 'Piek gelijktijdig',
    averageSession: 'Gem. sessie',
    listeningHours: 'Luisteruren',
    overTime: 'Verloop',
    chartLabel: 'Gemiddeld aantal luisteraars over het gekozen venster',
    byPlayer: 'Per speler',
    byCountry: 'Per land',
    byMount: 'Per stream',
    byContent: 'Per uitzending',
    columnContent: 'Uitzending',
    columnAired: 'Uitgezonden',
    columnAverage: 'Gemiddeld',
    columnPeak: 'Piek',
    caption: 'Luisteraars per uitgezonden item',
    emptyTitle: 'Nog geen gegevens',
    emptyDetail: 'In dit venster is niets uitgezonden of gemeten.',
  },

  archive: {
    title: 'Uitzendarchief',
    intro:
      'Wat er werkelijk uitging, dag voor dag, uit het as-run-logboek. Wat gepland stond maar niet uitzond, hoort hier juist níét te staan.',
    day: 'Dag',
    total: '{count} items · {duration}',
    columnTime: 'Tijd',
    columnWhat: 'Uitzending',
    columnType: 'Soort',
    columnLength: 'Duur',
    caption: 'As-run-logboek',
    emptyTitle: 'Niets in het archief voor deze dag',
    emptyDetail: 'Er is voor deze dag geen as-run-registratie.',
    airplayTitle: 'Afspeellijst voor rapportage (ISRC)',
    airplayIntro:
      'Elk uitgezonden nummer in een periode, met tijdstip, duur, artiest, titel en ISRC, als CSV voor Buma/Stemra en Sena. Een nummer zonder code staat erin op artiest en titel.',
    airplayFrom: 'Van',
    airplayTo: 'Tot en met',
    airplayDownload: 'CSV downloaden',
  },

  uploads: {
    title: 'Uploads',
    intro:
      'Sleep audio hierheen. Elk bestand wordt na aankomst geanalyseerd; pas als dat slaagt is het in te plannen, en wat niet decodeert gaat in quarantaine in plaats van de lucht in. Een export uit ShioCaster (mp3 + .metadata.json met dezelfde naam) wordt als één geheel herkend: het titelspoor stuurt dan de streamtitel tijdens de uitzending. Een losse mp3 werkt zoals altijd.',
    dropLabel: 'Audiobestanden uploaden',
    dropHere: 'Sleep audio hierheen of klik om te kiezen',
    dropDetail:
      'Meerdere bestanden tegelijk mag. Een .metadata.json naast de mp3 wordt automatisch gekoppeld. Duplicaten worden herkend en niet dubbel opgeslagen.',
    inProgress: 'Bezig',
    state: {
      uploading: 'Uploaden',
      processing: 'Verwerken',
      done: 'Klaar',
      duplicate: 'Bestond al',
      failed: 'Mislukt',
    },
    recent: 'Recente uploads',
    columnFile: 'Bestand',
    columnWhen: 'Geüpload',
    columnAnalysis: 'Analyse',
    caption: 'Recente uploads',
    emptyTitle: 'Nog geen uploads',
    emptyDetail: 'Upload audio om de bibliotheek te vullen.',
    columnPack: 'Episode-pack',
    packNone: 'Geen (klassiek)',
    packMismatch: 'Duur klopt niet',
    packTitles: '{count} titelwissels',
    packUnknownSongs: '{count} nummer(s) zonder naam',
    packNoAudio:
      'Geen geüploade audio met de naam “{stem}” gevonden. Upload de mp3 eerst of samen met dit bestand.',
  },

  fallback: {
    title: 'Fallback & noodbron',
    intro:
      'De keten is: gepland → rotatie → noodbron → de fallback-mount van Icecast zelf. Deze pagina laat zien welke schakel de uitzending nu draagt en houdt de schakelaars voor de noodbron vast.',
    emergencyActive: 'De noodbron is actief en overschrijft elk schema.',
    fallbackCarrying: 'De fallback draagt de uitzending.',
    mode: 'Modus',
    chain: 'Wat er nu draagt',
    chainScheduled: 'Geplande audio',
    chainUnknown: 'De runtime kon niet bevraagd worden',
    chainLive: 'Live-bron',
    chainFallback: 'Fallback',
    chainEmergency: 'Noodbron',
    emergencyControls: 'Noodbron',
    emergencyDetail:
      'Activeren onderbreekt de programmering en zet de noodset op de zender. Beide kanten op is een reden verplicht en wordt vastgelegd.',
    standDown: 'Terug naar automatisering',
    settings: 'Instellingen',
    settingEnabled: 'Noodbron beschikbaar',
    settingSilence: 'Stilte-alarm na',
    settingTimeout: 'Bron-timeout',
    settingFreeze: 'Bevriezingsvenster vóór uitzending',
    settingsNote:
      'De drempels zelf worden beheerd op de instellingenpagina; wijzig ze niet tijdens een incident.',
    toggle: 'Omzetten',
  },

  settings: {
    title: 'Instellingen',
    intro:
      'Per sectie, per sleutel. Wat het platform uit eigen gedrag afleidt, weigert de API te wijzigen — dat is beleid, geen ontbrekend invoerveld. Elke wijziging komt in het audit-logboek.',
    section: 'Sectie',
    sections: {
      broadcast: 'Uitzending',
      streams: 'Streams',
      playout: 'Playout',
      live_studio: 'Live studio',
      storage: 'Opslag',
      audio_processing: 'Audiobewerking',
      reliability: 'Betrouwbaarheid',
      integrations: 'Koppelingen',
    },
    emptySection: 'Deze sectie heeft nog geen instellingen.',
    saved: 'Opgeslagen',
  },

  clocks: {
    title: 'Klokken',
    intro:
      'De vorm van een uur. De cirkel is hoe je over een uur denkt; de tabel is hoe je hem precies maakt. Een item op exacte tijd start op zijn minuut, wat de rest van het uur ook deed.',
    which: 'Klokken',
    off: 'Uit',
    create: 'Nieuwe klok',
    createConfirm: 'Klok aanmaken',
    fieldName: 'Naam',
    fieldSlug: 'Slug',
    empty: 'Nog geen klokken. Maak er een aan om een uur vorm te geven.',
    minutesUsed: '{used} van 60 minuten ingevuld',
    overAnHour: 'Meer dan een uur',
    enable: 'Inschakelen',
    disable: 'Uitschakelen',
    assignments: 'Toewijzingen',
    noAssignments: 'Nog niet aan uren toegewezen.',
    everyDay: 'Elke dag',
    day: { mon: 'ma', tue: 'di', wed: 'wo', thu: 'do', fri: 'vr', sat: 'za', sun: 'zo' },
    elements: 'Elementen',
    columnStart: 'Start (min)',
    columnDuration: 'Duur (min)',
    columnLabel: 'Label',
    columnType: 'Soort',
    columnExact: 'Exact',
    columnFlexible: 'Flexibel',
    columnActions: 'Acties',
    newElement: 'Nieuw element',
    addElement: 'Element toevoegen',
    remove: 'Verwijderen',
    exactNote:
      'Exact = start op de minuut, wat er ook uitloopt. Flexibel = mag worden ingekort om uitloop op te vangen.',
    dialLabel: 'Uurklok: de vorm van één uitzenduur',
    hub: 'UUR',
  },

  liveStudio: {
    title: 'Live studio',
    intro:
      'Wie er nu verbonden is, hoe je verbindt, en de ene destructieve knop: een live-bron loskoppelen, waarmee de automatisering het overneemt.',
    now: 'Nu verbonden',
    nobody: 'Er is nu geen live-bron verbonden. De automatisering draagt de uitzending.',
    since: 'Verbonden sinds',
    software: 'Software',
    format: 'Formaat',
    disconnect: 'Loskoppelen',
    disconnectConfirm: 'Bron loskoppelen',
    howToConnect: 'Zo verbind je',
    connectIntro:
      'Elke encoder die het Icecast-bronprotocol spreekt werkt: BUTT, Mixxx, OBS met een Icecast-uitgang.',
    server: 'Server',
    port: 'Poort',
    mount: 'Mount',
    credentials: 'Inloggegevens',
    credentialsDetail: 'De eigen DJ-gebruikersnaam en het eenmalig getoonde wachtwoord.',
    takeoverNote:
      'Een geautoriseerde bron neemt direct over; bij wegvallen keert de automatisering vanzelf terug. Stilte is nooit de uitkomst.',
    studioTitle: 'ShioCaster en AzuraCast-compatibele software',
    studioIntro:
      'Het paneel beantwoordt de AzuraCast-API die ShioCaster en vergelijkbare software spreken. Plak de stream-URL hieronder in de studio; die leidt het station en de API eruit af.',
    studioStreamUrl: 'Stream-URL voor de studio',
    studioLive: 'Live-ingang (host:poort · mount)',
    studioNowPlaying: 'Nu te horen',
    studioTitlePush: 'Titelpush (Broadcast OS-modus)',
    studioKeys: 'API-sleutels',
    studioKeysDetail:
      'De skip-knop vraagt een sleutel met de scope playout:control; de titelpush live:broadcast. Beide gaan in de header X-API-Key.',
    studioKeysLink: 'Sleutel aanmaken',
    studioNote:
      'Zolang een presentator live is, stuurt de studio elke titelwissel met de DJ-inloggegevens naar de live-ingang; de luisteraar ziet het meteen. De ISRC in een titelpush wordt vastgelegd voor het airplay-rapport en komt nooit op de stream.',
    history: 'Sessies',
    columnConnected: 'Wanneer',
    columnWho: 'DJ',
    columnStatus: 'Status',
    columnFormat: 'Formaat',
    columnRecording: 'Opname',
    recorded: 'Opgenomen',
    caption: 'Live-sessies',
    emptyTitle: 'Nog geen live-sessies',
    emptyDetail: 'Zodra een DJ verbindt, verschijnt de sessie hier.',
    status: {
      CONNECTED: 'Verbonden',
      DISCONNECTED: 'Losgekoppeld',
    },
  },

  notifications: {
    title: 'Alarmering per SMS & telefoon',
    intro:
      'Wie wordt gebeld als het misgaat. Een melding gaat per SMS naar iedereen op de lijst; blijft een kritieke melding onbevestigd, dan belt het paneel. Antwoord OK op de SMS of druk 1 tijdens het gesprek om te bevestigen. De inloggegevens van de provider staan op de server, niet hier.',
    providerTitle: 'Provider',
    providerState: 'Status',
    providerConfigured: 'Twilio gekoppeld',
    providerNotConfigured: 'Niet ingesteld',
    providerSmsFrom: 'SMS-afzender',
    providerVoiceFrom: 'Belnummer',
    providerWebhooks: 'Webhook voor inkomende SMS',
    providerWebhooksHint:
      'Stel dit adres in als “A message comes in” op het Twilio-nummer. Bezorgstatus en toetsdruk komen op /status en /voice/ack van dezelfde basis.',
    providerMissingTitle: 'Er wordt niets verstuurd: Twilio is niet ingesteld',
    providerMissingDetail:
      'Zet TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN en TWILIO_SMS_FROM in het .env-bestand op de server en herstart de API en de worker. Tot die tijd worden meldingen wel vastgelegd, maar niet bezorgd. De uitzending is hier nooit van afhankelijk.',
    policyTitle: 'Beleid',
    policySms: 'SMS bij een melding',
    policyVoice: 'Bellen als een kritieke melding onbevestigd blijft',
    policyVoiceAfter: 'Bellen na (minuten)',
    policyAllClear: 'SMS als de melding is opgelost',
    policyLanguage: 'Taal van berichten en gesprek',
    policySaved: 'Beleid opgeslagen',
    create: 'Contact toevoegen',
    createConfirm: 'Toevoegen',
    fieldName: 'Naam',
    fieldPhone: 'Telefoonnummer',
    fieldThreshold: 'Vanaf ernst',
    fieldSms: 'SMS',
    fieldVoice: 'Bellen',
    thresholdCritical: 'Kritiek',
    thresholdWarning: 'Waarschuwing en hoger',
    columnName: 'Naam',
    columnPhone: 'Nummer',
    columnThreshold: 'Vanaf',
    columnSms: 'SMS',
    columnVoice: 'Bellen',
    columnState: 'Status',
    contactsCaption: 'Contacten',
    emptyTitle: 'Nog geen contacten',
    emptyDetail: 'Zonder contacten wordt niemand gebeld. Voeg ten minste één nummer toe.',
    on: 'Aan',
    off: 'Uit',
    testSms: 'Test-SMS',
    testCall: 'Testoproep',
    testSmsSent: 'Test-SMS verstuurd naar {name}',
    testCallPlaced: 'Testoproep gestart naar {name}',
    testFailed: 'De test is niet verstuurd.',
    enable: 'Inschakelen',
    disable: 'Uitschakelen',
    remove: 'Verwijderen',
    removeConfirm: 'Contact verwijderen',
    deliveriesTitle: 'Verstuurd',
    deliveriesCaption: 'Recente berichten en oproepen',
    deliveriesEmptyTitle: 'Nog niets verstuurd',
    deliveriesEmptyDetail:
      'Hier verschijnt elk bericht en elke oproep, ook als de bezorging mislukte.',
    columnWhen: 'Wanneer',
    columnChannel: 'Kanaal',
    columnKind: 'Soort',
    columnAlert: 'Melding',
    columnStatus: 'Status',
    channelSms: 'SMS',
    channelVoice: 'Oproep',
    kinds: {
      raised: 'Melding',
      escalated: 'Escalatie',
      cleared: 'Opgelost',
      test: 'Test',
    },
    statuses: {
      pending: 'In wachtrij',
      retry: 'Opnieuw',
      sent: 'Verstuurd',
      failed: 'Mislukt',
      abandoned: 'Vervallen',
    },
  },

  apiKeys: {
    title: 'API-sleutels',
    intro:
      'Een sleutel is een langlopende toegang voor een koppeling: de Prometheus-scrape, een script, een apparaat dat de API bevraagt. Hij handelt namens jou met alleen de rechten die je kiest, en werkt niet meer zodra je hem intrekt of jouw account is uitgeschakeld. De sleutel zelf zie je één keer.',
    create: 'Nieuwe sleutel',
    createConfirm: 'Sleutel aanmaken',
    fieldName: 'Naam',
    namePlaceholder: 'bijv. prometheus',
    fieldExpiry: 'Verloopt na (dagen, leeg = nooit)',
    fieldScopes: 'Rechten',
    scopesHint: 'Alleen rechten die je zelf hebt. Kies zo min mogelijk.',
    columnAccess: 'Toegang',
    fieldAccess: 'Beperken tot shows en presentatoren',
    accessHint:
      'Niets aangevinkt: de sleutel doet wat jij mag. Iets aangevinkt: de sleutel ziet en bedient alleen de aangevinkte shows en presentatoren, hun afleveringen en sessies, en de zender zolang een van hen te horen is. Dat is wat een studio of de software van één presentator hoort te krijgen.',
    accessShows: 'Shows',
    accessStreamers: 'Presentatoren',
    accessAll: 'Alles wat jij mag',
    columnName: 'Naam',
    columnPrefix: 'Begint met',
    columnScopes: 'Rechten',
    columnLastUsed: 'Laatst gebruikt',
    columnExpires: 'Verloopt',
    columnState: 'Status',
    caption: 'API-sleutels',
    emptyTitle: 'Nog geen sleutels',
    emptyDetail: 'Maak er een aan voor een koppeling die zonder inlogsessie moet werken.',
    neverUsed: 'Nog nooit',
    noExpiry: 'Nooit',
    revoke: 'Intrekken',
    revokeConfirm: 'Sleutel intrekken',
    revokedOn: 'Ingetrokken {date}',
    issued: 'Sleutel “{name}” aangemaakt',
    issuedOnce: 'Wordt één keer getoond. Bewaar hem nu; later is alleen het begin nog zichtbaar.',
  },

  playlistStudio: {
    title: 'Playlist Studio',
    intro:
      'De AI-playlistgenerator, per show en per aflevering. Kies het programma en eventueel de aflevering die je voorbereidt; de studio opent met die opdracht ingevuld en met de huisregels, seed-artiesten en show-klok van datzelfde programma. Wat hij op Spotify publiceert komt hieronder bij de aflevering te staan.',
    notConfiguredTitle: 'Playlist Studio draait niet op deze server',
    notConfiguredDetail:
      'Start de overlay compose.playlist-studio.yml met PLAYLIST_STUDIO_TOKEN en PLAYLIST_STUDIO_WEBHOOK_SECRET in .env. De Spotify- en LLM-sleutels stel je daarna één keer in op de Settings-pagina van de studio, hier in het paneel.',
    unreachableTitle: 'Playlist Studio is ingesteld maar antwoordt niet',
    fieldShow: 'Programma',
    fieldEpisode: 'Aflevering',
    noEpisode: 'Geen aflevering (alleen het programma)',
    reachable: 'Verbonden ({version})',
    unreachable: 'Onbereikbaar',
    spotifyConnected: 'Spotify gekoppeld',
    spotifyNotConnected: 'Spotify nog niet gekoppeld',
    llmConfigured: 'AI ingesteld',
    llmNotConfigured: 'AI nog niet ingesteld',
    open: 'Studio openen',
    briefShow: 'De studio opent met het profiel en de huisregels van {show}.',
    briefEpisode:
      'De studio opent voor {show}, aflevering {episode}: titel, uitzenddatum en doelduur staan alvast ingevuld.',
    frameTitle: 'Playlist Studio',
    published: 'Playlist “{name}” gepubliceerd op Spotify',
    playlistsTitle: 'Playlists van dit programma',
    playlistsCaption: 'Gepubliceerde playlists',
    playlistsEmptyTitle: 'Nog geen playlists',
    playlistsEmptyDetail:
      'Genereer en publiceer er een in de studio; hij verschijnt hier bij de aflevering.',
    columnWhen: 'Wanneer',
    columnName: 'Naam',
    columnEpisode: 'Aflevering',
    columnTracks: 'Tracks',
    columnLink: 'Spotify',
    openSpotify: 'Openen',
    replaced: 'Bijgewerkt',
    minutes: '{count} min',
  },

  notFound: {
    nothingHere: 'Hier staat niets',
    notMatched: 'Dit adres hoort niet bij een pagina in het paneel.',
    title: 'Pagina niet gevonden',
    body: 'Deze pagina bestaat niet.',
    back: 'Terug naar de uitzending',
  },
} as const;
